import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  FileText, CheckCircle, AlertTriangle, RefreshCw, Save
} from 'lucide-react';
import AgentLayout from '../components/AgentLayout';
import api from '../services/api';

const AdminTradosAgentFiles = () => {
  const [files, setFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [editingFile, setEditingFile] = useState(null);
  const [fileContent, setFileContent] = useState('');

  const fetchFiles = useCallback(async () => {
    setFilesLoading(true);
    try {
      const res = await api.get('/admin/trados-agent/files');
      setFiles(res.data.files || []);
    } catch (err) { toast.error('Failed to load files'); }
    finally { setFilesLoading(false); }
  }, []);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const openFileEditor = async (fileId) => {
    try {
      const res = await api.get(`/admin/trados-agent/files/${fileId}`);
      setEditingFile(res.data);
      setFileContent(res.data.content || '');
    } catch (err) { toast.error('Failed to load file'); }
  };

  const saveFile = async () => {
    if (!editingFile) return;
    try {
      await api.put(`/admin/trados-agent/files/${editingFile.id}`, { content: fileContent });
      toast.success(`${editingFile.label} saved`);
      setEditingFile(null);
      setFileContent('');
      fetchFiles();
    } catch (err) { toast.error('Failed to save file'); }
  };

  return (
    <AgentLayout>
      <div className="space-y-4">
        {editingFile ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">{editingFile.label}</h3>
                <p className="text-sm text-gray-500">{editingFile.filename}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={saveFile}
                  className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">
                  <Save className="w-4 h-4" /> Save
                </button>
                <button onClick={() => { setEditingFile(null); setFileContent(''); }}
                  className="px-4 py-2 bg-white text-gray-600 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">
                  Cancel
                </button>
              </div>
            </div>
            <textarea value={fileContent} onChange={(e) => setFileContent(e.target.value)}
              className="w-full h-[calc(100vh-360px)] px-4 py-3 border border-gray-300 rounded-xl text-sm font-mono focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
              spellCheck={false} />
          </div>
        ) : (
          <>
            <h3 className="text-lg font-semibold text-gray-900">Agent Knowledge Base Files</h3>
            {filesLoading ? (
              <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center py-12 text-gray-400">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading files...
              </div>
            ) : (
              <div className="space-y-4">
                {['trados', 'intel', 'user', 'public', 'shared'].map(agentGroup => {
                  const agentFiles = files.filter(f => f.agent === agentGroup);
                  if (agentFiles.length === 0) return null;
                  const groupLabels = { trados: 'Trados', intel: 'Intel', user: 'Amanda', public: 'Brandon', shared: 'Shared' };
                  return (
                    <div key={agentGroup}>
                      <p className="text-sm font-medium text-gray-500 mb-2">{groupLabels[agentGroup]} Agent</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {agentFiles.map(f => (
                          <button key={f.id} onClick={() => openFileEditor(f.id)}
                            className="bg-white rounded-xl border border-gray-200 p-4 text-left hover:border-indigo-300 hover:bg-indigo-50/30 transition-all">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                                <FileText className="w-5 h-5 text-gray-500" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold text-gray-900">{f.label}</p>
                                <p className="text-xs text-gray-500">{f.filename} · {(f.size / 1024).toFixed(1)} KB</p>
                              </div>
                              {f.exists ? (
                                <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                              ) : (
                                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </AgentLayout>
  );
};

export default AdminTradosAgentFiles;
